import styled from 'styled-components';
import PropTypes from 'prop-types';
import {
  CheckCircleOutlined,
  EuroOutlined,
  HomeOutlined,
  IdcardOutlined
} from '@ant-design/icons';
import { handleNotifMenuItemLink } from '../../../../../components/Notifications/handleNotificationMenuLink';
import { useDeleteNotification } from '../../../../../utils/deleteNotification';
import { isUnreadNotification } from '../../../utils/alertColumns';

const ICONS = {
  HomeOutlined,
  EuroOutlined,
  IdcardOutlined
};

const StyledColumn = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 240px;
  padding: 14px;
  background: var(--ds-column-bg);
  border-radius: var(--ds-radius-xl);
`;

const StyledHeader = styled.header`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 6px;
`;

const StyledIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: var(--ds-radius-sm);
  font-size: 15px;
  background: ${({ $tint }) => $tint};
  color: ${({ $ink }) => $ink};
`;

const StyledTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
`;

const StyledUnread = styled.span`
  padding: 2px 8px;
  border-radius: var(--ds-radius-md);
  font-size: 11px;
  font-weight: 700;
  background: var(--pear);
  color: var(--darkGray);
`;

const StyledTotal = styled.span`
  margin-left: auto;
  font-size: 13px;
  font-weight: 600;
  color: var(--dimGray);
`;

const StyledEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 0;
  font-size: 13px;
  color: var(--dimGray);

  .anticon {
    font-size: 22px;
    color: var(--cambridge);
  }
`;

/**
 * AlertColumn component displaying the alerts of one category.
 * @component
 * @param {Object} props - The properties of the AlertColumn component.
 * @param {Object} props.column - Column description (see ALERT_COLUMNS).
 * @param {Array} props.notifications - Notifications of the column.
 * @param {Function} props.t - Translation function for internationalization.
 * @param {Function} props.dispatchAPI - Function for dispatching API calls.
 * @param {Object} props.user - User object containing user information.
 * @param {boolean} props.forceRefresh - Boolean flag for forcing a data refresh.
 * @param {Function} props.setForceRefresh - Function to set the forceRefresh state.
 * @param {Function} props.message - Function for displaying messages.
 * @returns {JSX.Element} - JSX element representing the AlertColumn component.
 */
export const AlertColumn = ({
  column,
  notifications,
  t,
  dispatchAPI,
  user,
  forceRefresh,
  setForceRefresh,
  message
}) => {
  const { deleteNotification } = useDeleteNotification({
    dispatchAPI,
    forceRefresh,
    setForceRefresh,
    message
  });

  const Icon = ICONS[column.icon];
  const unreadCount = notifications.filter((notification) =>
    isUnreadNotification(notification, user._id)
  ).length;

  return (
    <StyledColumn>
      <StyledHeader>
        <StyledIcon $tint={column.tint} $ink={column.ink}>
          <Icon />
        </StyledIcon>
        <StyledTitle>{t(`notifications.columns.${column.key}`)}</StyledTitle>
        {unreadCount > 0 && (
          <StyledUnread>
            {t('notifications.unread_count', { count: unreadCount })}
          </StyledUnread>
        )}
        <StyledTotal>{notifications.length}</StyledTotal>
      </StyledHeader>
      {notifications.length === 0 ? (
        <StyledEmpty>
          <CheckCircleOutlined />
          {t('notifications.no_alert')}
        </StyledEmpty>
      ) : (
        notifications.map((item) => (
          <div key={item._id}>
            {handleNotifMenuItemLink(
              item._id,
              deleteNotification,
              t,
              item.created_at,
              item.consulted,
              item.item,
              'card'
            )}
          </div>
        ))
      )}
    </StyledColumn>
  );
};

AlertColumn.propTypes = {
  column: PropTypes.shape({
    key: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    tint: PropTypes.string.isRequired,
    ink: PropTypes.string.isRequired
  }).isRequired,
  notifications: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  t: PropTypes.func.isRequired,
  dispatchAPI: PropTypes.func.isRequired,
  forceRefresh: PropTypes.bool.isRequired,
  setForceRefresh: PropTypes.func.isRequired,
  message: PropTypes.func.isRequired,
  user: PropTypes.shape({
    _id: PropTypes.string
  }).isRequired
};
